import imageCompression from 'browser-image-compression'
import { PDFDocument } from 'pdf-lib'

async function renderPdfToImages (file) {
  const pdfData = await file.arrayBuffer()
  const pdf = await window.pdfjsLib.getDocument({ data: pdfData }).promise

  const images = []

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum)

    const viewport = page.getViewport({ scale: 2 }) // ↑ scale = better quality

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.width = `${viewport.width}px`
    canvas.style.height = `${viewport.height}px`

    await page.render({ canvasContext: ctx, viewport }).promise

    const image = await new Promise(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', 1)
    )

    images.push({
      image: image,
      height: viewport.height,
      width: viewport.width
    })
  }

  return images
}

async function compressImages (images, originalFileSize, compressProgressCallback = null) {
  const imageLength = images.length
  const maxSize = (originalFileSize / imageLength) / (imageLength / 2)
  const currentProgress = {}

  return Promise.all(
    images.map((file, key) => {
      const maxFileSizeMB = 0.45
      const fileSize = file.image.size / 1024 / 1024 // Convert to MB

      const fileSizeExceedsLimit = fileSize > maxFileSizeMB

      const useBackgroundProcessor = {
        maxIteration: fileSizeExceedsLimit ? 7 : 1,
        maxSizeMB: maxSize > 0 ? Math.min(maxSize, maxFileSizeMB) : maxFileSizeMB,
        useWebWorker: typeof Worker === 'function',
        maxWidthOrHeight: Math.max(file.width, file.height),
        onProgress: progress => {
          if (typeof compressProgressCallback === 'function' || compressProgressCallback instanceof Function) {
            currentProgress[key] = progress

            const totalProgress = Object.values(currentProgress).reduce(
              (total, current) => total + current,
              0
            ) / imageLength

            compressProgressCallback(Math.min(totalProgress / images.length * 100, 100))
          }
        }
      }

      return imageCompression(file.image, useBackgroundProcessor)
    })
  )
}

async function renderImageToPdf (images) {
  const pdfDoc = await PDFDocument.create()

  for (const img of images) {
    const bytes = await img.arrayBuffer()
    const image = await pdfDoc.embedJpg(bytes)

    const page = pdfDoc.addPage([image.width, image.height])

    page.drawImage(image, {
      x: 0,
      y: 0,
      width: image.width,
      height: image.height
    })
  }

  return await pdfDoc.save()
}

async function pdfCompressor (file, compressProgressCallback = null) {
  const images = await renderPdfToImages(file)
  const compressedImages = await compressImages(images, file.size, compressProgressCallback)
  const pdfBytes = await renderImageToPdf(compressedImages)
  const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' })

  const pdfFile = new File(
    [pdfBlob],
    file.name,
    { type: 'application/pdf' }
  )

  return pdfFile
}

export {
  pdfCompressor
}
